import React, { FC, useState } from 'react'
import DocumentTitle from 'react-document-title'
import { Button } from 'transfix-ui/components/Button'
import { Box, Typography } from 'transfix-ui/core'
import {
  GetStaplesHeadhaulLanesQueryParams,
  StaplesHeadhaulLane,
  StaplesHeadhaulLanesFilters,
  useGetStaplesHeadhaulLanes,
} from 'apps/marketplace/apiTypes/staplesHeadhaulLanes'
import { Pagination, Spinner } from 'transfix-ui'
import { generatePageTitle } from 'utils/generateAppDefaults'
import { useModal } from 'utils/hooks'
import { DeleteConfirmationModal } from './StaplesDeleteLaneModal'
import StaplesHeadhaulLanesFilter from './StaplesHeadhaulLanesFilters'
import { StaplesHeadhaulLanesTable } from './StaplesHeadhaulLanesTable'
import { StaplesLaneModal } from './StaplesLaneModal'

export type StaplesLaneModals = 'staplesLaneModal'

const StaplesHeadhaulLanes: FC = () => {
  const [requestParams, setRequestParams] = useState<GetStaplesHeadhaulLanesQueryParams>({
    filters: {},
    pageCount: 0,
  })

  const [deleteLaneId, setDeleteLaneId] = useState<number | null>(null)
  const [editLaneId, setEditLaneId] = useState<number | null>(null)

  const [editStaplesHeadhaulLane, setEditStaplesHeadhaulLane] = useState<
    StaplesHeadhaulLane | undefined
  >()

  const { closeModal, isOpen, openModal } = useModal<StaplesLaneModals>()

  const { data, loading, refetch } = useGetStaplesHeadhaulLanes({
    queryParams: {
      filters: requestParams.filters,
      pageCount: requestParams.pageCount || 0,
    },
  })

  const { staplesHeadhaulLanes = [], totalCount = 0 } = data || {}

  const setFilters = (filters: Partial<StaplesHeadhaulLanesFilters>) =>
    setRequestParams(prevParams => ({
      ...prevParams,
      filters: {
        ...prevParams.filters,
        ...filters,
      },
    }))

  const onPageChange = (pageCount: number) => {
    setRequestParams(prevParams => ({
      ...prevParams,
      pageCount: pageCount - 1,
    }))
  }

  return (
    <DocumentTitle title={generatePageTitle('Staples Headhaul Lanes')}>
      <Box p={4}>
        <Box mb={2} display='flex' alignItems='center'>
          <Box flexGrow={1}>
            <Typography variant='h2'>Staples Headhaul Lanes</Typography>
          </Box>
          <Box>
            <Button
              onClick={openModal('staplesLaneModal')}
              color='primary'
              id='store-data'
              data-test='store-data'
              size='large'>
              Add Staples Lane
            </Button>
          </Box>
        </Box>
        <Box mb={2}>
          <StaplesHeadhaulLanesFilter onSetFilters={setFilters} />
        </Box>
        {loading && <Spinner position='absolute' />}
        {!loading && (
          <>
            <Box mb={2}>
              Displaying {staplesHeadhaulLanes.length} of {totalCount || 0} Staples Headhaul Lanes
            </Box>
            <Box mb={2}>
              <StaplesHeadhaulLanesTable
                staplesHeadhaulLanes={staplesHeadhaulLanes}
                onOpenDeleteConfirmation={id => setDeleteLaneId(id)}
                onOpenEditConfirmation={(id, staplesHeadhaulLane) => {
                  setEditLaneId(id)
                  setEditStaplesHeadhaulLane(staplesHeadhaulLane)
                }}
              />
            </Box>
            <Box textAlign='center'>
              <Pagination
                currentPage={(requestParams.pageCount || 0) + 1}
                numberOfPages={Math.ceil(totalCount / 20)}
                onPageChange={onPageChange}
              />
            </Box>
            {isOpen('staplesLaneModal') && (
              <StaplesLaneModal
                laneIdToUpdate={-1}
                isEdit={false}
                onClose={closeModal}
                onRefetch={refetch}
              />
            )}
          </>
        )}
        {deleteLaneId && (
          <DeleteConfirmationModal
            laneId={deleteLaneId}
            open={!!deleteLaneId}
            onRefetch={refetch}
            onClose={() => setDeleteLaneId(null)}
          />
        )}
        {editLaneId && (
          <StaplesLaneModal
            isEdit
            laneIdToUpdate={editLaneId}
            existingData={editStaplesHeadhaulLane}
            onClose={() => setEditLaneId(null)}
            onRefetch={refetch}
          />
        )}
      </Box>
    </DocumentTitle>
  )
}

export default StaplesHeadhaulLanes
